import { ImagePaths } from '../../lib/imageUrls.js';

export const sharepointInformationArchitect = {
  id: 26,
  slug: 'sharepoint-information-architect',
  ogImage: '/og/courses/sharepoint-information-architect.jpg',
  courseCode: 'SHARECOP-ARCH-001',
  title: 'SharePoint Information Architect — Enterprise IA for Copilot',
  subtitle: 'The 1-day enterprise IA workshop for SharePoint architects designing for Copilot',
  valueProposition: 'Move beyond site ownership — design the hub hierarchies, taxonomy frameworks, and governance models that make SharePoint a reliable, AI-ready knowledge platform for your entire organisation.',
  duration: '8 hours',
  students: 'New',
  level: 'Advanced',
  organization: 'EDUC4TE',
  image: ImagePaths.COURSE_3,
  shortDescription: 'SHARECOP-ARCH-001 — Enterprise SharePoint IA for Copilot covering hub topology, term store, content types, and migration IA design in one intensive day.',

  price: {
    aud: 895,
    currency: 'AUD',
    earlyBird: 795
  },

  series: {
    name: 'EDUC4TE SharePoint Copilot Series',
    position: 'Enterprise Architecture (advanced)',
    description: 'Three-course modular series preparing SharePoint environments for Microsoft 365 Copilot — Foundation, Governance, and Enterprise Architecture.',
    courses: [
      { code: 'SHARECOP-FND-001', title: 'SharePoint Copilot Readiness — Foundation', slug: 'sharepoint-copilot-readiness-foundation' },
      { code: 'SHARECOP-GOV-001', title: 'SharePoint Copilot Governance — SAM, RCD and Permission Remediation', slug: 'sharepoint-copilot-in-a-day' },
      { code: 'SHARECOP-ARCH-001', title: 'SharePoint Information Architect — Enterprise IA for Copilot', slug: 'sharepoint-information-architect' }
    ],
    bundle: { code: 'SHARECOP-BUNDLE-001', name: '3-Day SharePoint Copilot Series Bundle', aud: 1995, savings: 'Save AUD 390 (~16%) vs individual' },
    contact: 'info@educ4te.com'
  },

  keyDeliverable: {
    title: 'Enterprise IA Blueprint',
    description: 'Participants draft an Enterprise IA Blueprint during the final workshop — hub topology, term store, content-type register, permission model, migration IA decisions, Copilot readiness scores, and governance programme design. Yours to keep and refine post-course.'
  },

  stripeConfig: {
    priceId: 'price_1U3WMmKu1nyc322UpaYu8hzg',
    productId: 'prod_V3dekes3Egl3LP'
  },

  snapshot: {
    whoItsFor: [
      'SharePoint administrators taking on enterprise architect responsibilities',
      'IT managers designing organisation-wide M365 governance frameworks',
      'Digital workplace leads planning large-scale SharePoint migrations',
      'Governance and compliance officers responsible for M365 information management'
    ],
    accreditation: 'Aligned to Microsoft 365 Information Architecture guidance, Australian Privacy Act 1988, Australian Privacy Principles (APPs), ACSC Microsoft 365 security guidance, and ASD Blueprint for Secure Cloud'
  },

  logistics: {
    classSize: 'Maximum 16 participants for collaborative design workshops',
    requirements: [
      'Completion of SharePoint Site Owners course or equivalent experience (minimum 6 months site ownership)',
      'SharePoint Online admin permissions on a Microsoft 365 tenant',
      'Familiarity with content types, site columns, and managed metadata basics',
      'Understanding of your organisation\'s current SharePoint structure and governance gaps'
    ]
  },

  overview: 'The SharePoint Information Architect course is the advanced capstone of the SharePoint Copilot series, designed for practitioners who need to think — and operate — at the enterprise scale. Where site ownership focuses on individual sites, information architecture addresses the entire M365 estate: how hub hierarchies connect content, how managed metadata creates a consistent taxonomy across hundreds of sites, how content type governance ensures document standards without constraining teams, and how migration from legacy network drives or classic SharePoint is planned and executed safely. A central theme throughout is Copilot optimisation: Microsoft 365 Copilot retrieves responses through semantic search and retrieval-augmented generation — and the accuracy of those responses depends directly on the quality of your information architecture. This intensive one-day workshop equips participants to design IA frameworks that maximise Copilot signal, reduce permission noise, and keep content trustworthy at scale. Australian context includes Privacy Act 1988 obligations for information classification, ACSC guidance for M365 configuration, and ASD Blueprint alignment for enterprise deployments. Participants leave with a practical enterprise IA blueprint and governance model ready for executive endorsement.',

  outcomes: [
    'Design hub site hierarchies that reflect organisational structure and support cross-site content discovery',
    'Build and govern a managed metadata term store that scales across an entire Microsoft 365 tenant',
    'Create and deploy content type hub policies that enforce document standards organisation-wide',
    'Plan and execute structured migrations from network drives or classic SharePoint to modern SharePoint Online',
    'Optimise SharePoint information architecture to maximise Microsoft 365 Copilot response accuracy',
    'Apply information classification frameworks aligned to Australian Privacy Act 1988 and ACSC guidance',
    'Establish an enterprise SharePoint governance programme with roles, review cycles, and escalation paths',
    'Produce an enterprise IA blueprint and governance model ready for executive sign-off'
  ],

  audience: [
    'SharePoint administrators responsible for enterprise-wide M365 governance',
    'IT architects designing digital workplace platforms for mid-to-large Australian organisations',
    'SharePoint site owner leads who have outgrown per-site thinking and need to govern at scale',
    'Compliance and records management professionals implementing information classification in M365',
    'Project managers leading SharePoint Online migration or consolidation programmes'
  ],

  prerequisites: [
    'SHARECOP-FND-001 (SharePoint Copilot Readiness — Foundation) OR 6+ months active SharePoint Online administration experience',
    'Working knowledge of content types and managed metadata required',
    'SharePoint Online admin access required for lab exercises',
    'Familiarity with at least one SharePoint governance challenge in your current environment'
  ],

  modules: [
    {
      title: 'Module 1: Hub Topology & Enterprise IA Principles',
      duration: '2 hours',
      topics: [
        'Information architecture vs site administration — scope, responsibilities, and governance boundaries',
        'Hub site hierarchies: when to flatten, when to nest, and when to federate',
        'Mapping organisational structure to hub topology — common Australian enterprise patterns',
        'Designing for findability, Copilot accuracy, and long-term maintainability',
        'IA anti-patterns: over-nesting, permission proliferation, and taxonomy sprawl'
      ]
    },
    {
      title: 'Module 2: Term Store & Content Type Architecture',
      duration: '2 hours',
      topics: [
        'Term store architecture: groups, term sets, terms, and synonyms — designed for a multi-division organisation',
        'Content type hub: publishing site columns and content types tenant-wide',
        'Metadata inheritance and enforcement — site, library, and column defaults',
        'Enterprise content type strategy: naming, hierarchy, ownership, and adoption auditing',
        'Sensitivity labels and retention policies — integrating Microsoft Purview with SharePoint IA',
        'Handling taxonomy drift: deprecation, reuse policies, and governance workflows'
      ]
    },
    {
      title: 'Module 3: IA Design for Migration & Copilot Optimisation',
      duration: '2 hours',
      topics: [
        'Design the target information architecture before migration begins — legacy content auditing and discovery',
        'Hub topology mapping from file shares, classic SharePoint, and Teams chaos',
        'Content type and metadata mapping from legacy folders into the target taxonomy',
        'Permission boundary decisions: what to flatten, what to preserve, what to deny',
        'How Microsoft 365 Copilot uses SharePoint semantic index and retrieval-augmented generation',
        'IA signals that improve Copilot accuracy: metadata groundedness, permission hygiene, content freshness',
        'Out of scope: tool operation (SPMT, ShareGate, Migration Manager) — participants are the architects, not the operators'
      ]
    },
    {
      title: 'Module 4: Enterprise IA Blueprint Workshop',
      duration: '2 hours',
      topics: [
        'Governance model components: roles, policies, review cycles, and escalation paths',
        'SharePoint governance committee structure for Australian mid-to-large enterprises',
        'Privacy Act 1988 obligations in enterprise IA — classification, retention, and right to erasure',
        'ACSC ISM and ASD Blueprint alignment for M365 enterprise configuration',
        'Hands-on workshop: draft the Enterprise IA Blueprint (hub topology, term store, content-type register, permission model, migration decisions, Copilot readiness scores, governance programme)',
        'Peer review and executive-endorsement framing for the blueprint document'
      ]
    }
  ],

  format: {
    type: 'Live instructor-led workshop, self-paced online modules with design exercises, or lunch-and-learn (8 x 1-hour sessions over 8 days)',
    includes: [
      '8 hours of instructor-led training delivered in a single intensive day',
      'Enterprise IA blueprint template (hub topology, term store, content type registry)',
      'SharePoint governance programme starter pack (roles, policies, review schedule)',
      'Hands-on labs using a Microsoft 365 tenant with admin permissions',
      'Migration IA design checklist and target-architecture hand-over template',
      'Copilot readiness IA review checklist',
      'Access to all materials and templates for 12 months',
      'Certificate of completion',
      'Email support for 30 days post-course'
    ]
  },

  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Full-day live workshop (9:00 AM to 5:30 PM AEST) or self-paced access available immediately'
  },

  faqs: [
    {
      question: 'Why is this now a 1-day course?',
      answer: 'We compressed the programme from four days to one intensive day to align with how leading Microsoft training vendors deliver enterprise IA workshops — focused IP, no padding. The core deliverable (Enterprise IA Blueprint) and the four critical themes (hub topology, term store and content types, migration IA design, governance) are all preserved. Organisations that need deeper, tenant-specific work can engage us for follow-up consulting rather than paying for extended classroom time.'
    },
    {
      question: 'Do I need to complete the earlier SharePoint courses first?',
      answer: 'Completing the SharePoint Site Owners course (Level 2) — or having equivalent site ownership experience — is strongly recommended. This course assumes you are already comfortable configuring libraries, permissions, and content types. If you are unsure whether your experience is sufficient, contact us and we will advise based on your current role.'
    },
    {
      question: 'Is this course suitable for organisations without a dedicated SharePoint admin?',
      answer: 'This course is most effective when participants have SharePoint Online admin or SharePoint admin permissions in their M365 tenant. For organisations without a dedicated SharePoint admin, the course works well for the IT generalist or M365 lead who has taken on governance responsibilities and needs to formalise their approach at scale.'
    },
    {
      question: 'How does this course differ from the SharePoint Copilot Governance course?',
      answer: 'The Governance course focuses on preparing a specific SharePoint environment for Copilot activation — SAM, RCD, and permission remediation at the tenant level. The Information Architect course takes an enterprise-wide design view: how to design the hub topology, taxonomy, and governance framework that makes Copilot accurate across the entire organisation, not just one tenant configuration pass.'
    },
    {
      question: 'We are planning a SharePoint migration from a network drive — is this the right course?',
      answer: 'Yes — for the IA design side of the migration. Module 3 covers designing the target information architecture before migration begins: legacy content auditing, hub topology mapping from file shares, content type and metadata mapping, permission boundary decisions, and the IA design document your migration team works from. Tool operation (SPMT, ShareGate, Migration Manager) is intentionally out of scope — you leave as the architect, not the operator.'
    },
    {
      question: 'Does the course cover Microsoft Purview and sensitivity labels?',
      answer: 'Yes, in the context of SharePoint information architecture. Module 2 covers how sensitivity labels and retention policies from Microsoft Purview integrate with SharePoint content types and libraries. For a deeper Purview-focused programme, we recommend pairing this course with our SharePoint Copilot Governance course.'
    },
    {
      question: 'Is this course relevant for Australian Privacy Act 1988 compliance?',
      answer: 'Yes. Information architecture is directly relevant to Privacy Act obligations — particularly around information classification, retention, and the right to erasure. Module 4 addresses how to incorporate APP-compliant classification and retention policies into your enterprise IA framework, and how to document your approach for regulatory purposes.'
    },
    {
      question: 'Can we run this as in-house training using our own SharePoint tenant?',
      answer: 'In-house delivery is strongly recommended for this course. Designing your actual hub hierarchy, term store, and governance model — using your real content and organisational structure — produces far more valuable outputs than working with a generic demo environment. Contact us to discuss scheduling and to prepare your tenant for the workshop.'
    },
    {
      question: 'Is private-cohort pricing available for multiple participants?',
      answer: 'Yes. For teams of 3 or more enrolling together, contact us for a private quote. For enterprise IA work, training your SharePoint admin, governance lead, and key site owners together ensures everyone operates from the same architectural principles and governance model.'
    },
    {
      question: 'Can this course be delivered as lunch-and-learn sessions?',
      answer: 'Yes. The course runs as eight one-hour sessions over eight days, with the same content and outcomes as the one-day format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on labs are spread across the sessions, so participants need access to the lab environment throughout. Contact us to arrange a schedule.'
    }
  ]
};
